// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Auth Service
// Handles email+password login, cookie-based JWT refresh, and logout.
//
// Flow: User enters email+password -> POST /api/auth/login -> JWT in HttpOnly
// cookies. CSRF double-submit token in csrf_access_token cookie.
// ---------------------------------------------------------------------------

import { useAuthStore } from '@/store/auth-store';

// ---------------------------------------------------------------------------
// CSRF helper
// ---------------------------------------------------------------------------

function getCsrfToken(): string {
  const match = document.cookie.match(/csrf_access_token=([^;]+)/);
  return match ? match[1] : '';
}

// ---------------------------------------------------------------------------
// Auth service
// ---------------------------------------------------------------------------

export const authService = {
  /**
   * Authenticate a user with email and password.
   * On success, the backend sets HttpOnly JWT cookies automatically.
   */
  async login(email: string, password: string) {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || data.error || 'Identifiants incorrects');
    if (data.requires_2fa) throw new Error('2FA non supporte sur cette interface');
    return data;
  },

  /**
   * Refresh the access token using the refresh cookie.
   * If refresh fails, logs the user out.
   */
  async refresh() {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-TOKEN': getCsrfToken() },
    });
    if (!res.ok) {
      useAuthStore.getState().logout();
      throw new Error('Session expiree');
    }
    return res.json();
  },

  /**
   * Fetch current user info from the /me endpoint.
   * Returns null if not authenticated.
   */
  async getMe() {
    const res = await fetch('/api/auth/me', {
      credentials: 'include',
      headers: { 'X-CSRF-TOKEN': getCsrfToken() },
    });
    if (!res.ok) return null;
    return res.json();
  },

  /**
   * Logout the current user. Clears auth store and best-effort server call.
   */
  async logout() {
    useAuthStore.getState().logout();
  },
};
